/*
 * @providesModule LTOComponent
 *
 */

import React from 'react';

import * as Animatable from 'react-native-animatable';
import AWSManager from 'AWSManager';
import _ from 'lodash';
import CheckoutController from 'CheckoutController';
import ClientPropertyConstants from 'ClientPropertyConstants';
import DepositOfferTile from 'DepositOfferTile';
import Divider from 'Divider';
import GlobalEventEmitter from 'GlobalEventEmitter';
import I18n, {_m} from 'INTL';
import Label from 'Label';
import LinearGradient from 'react-native-linear-gradient';
import LoadableComponent from 'LoadableComponent';
import moment from 'moment';
import NavigationManager from 'NavigationManager';
import NativeNavigation from 'NativeNavigation';
import NetClient from 'NetClient';
import Optional from 'Optional';
import OrientationTransitionController from 'OrientationTransitionController';
import PushButton from 'PushButton';

import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import SKZConstants from 'SKZConstants';
import styles from 'LTOComponentStyle';

import {AppState, Platform, TouchableOpacity, View} from 'react-native';
import SKZLayoutAnimations from 'SKZLayoutAnimations';
import {EmitterSubscription} from 'react-native';
import {NativeEventSubscription} from 'react-native';

const PROMO_BUMP_ANIMATION_DURATION = Platform.select({ios: 420, android: 400});
const PROMO_BUMP_ANIMATION_UPDATE_OFFSET = Platform.select({ios: 60, android: 50});

type ILTOComponentProps = {
  isInsufficientFunds: boolean;
  isCash: boolean;
  storeInfo: any | {};
  onDone: () => void;
  isStoreDisplayed: boolean;
  isPlayTabDisplayed: boolean;
  onClaimOffer: (storeInfo: any, offer: any) => void;
  appliedPromo: any;
  mainNavigator: any;
};

type ILTOComponenetState = {
  offer?: any;
  endsInText: string;
  willDeposit: boolean;
  displayedPromo: any;
  storeInfo?: any;
};

export default class LTOComponent extends React.Component<ILTOComponentProps, ILTOComponenetState> {
  isTriggeringOffer: boolean;
  didCompleteDeposit: boolean;
  appStateChangeListener?: NativeEventSubscription;
  tileButtonContainer: any;
  subscriptions: EmitterSubscription[] = [];
  offerCountdownTimer?: NodeJS.Timeout = undefined;
  defaultEndsInText: string;

  constructor(props: any) {
    super(props);
    this.isTriggeringOffer = false;
    this.didCompleteDeposit = false;
    this.appStateChangeListener = undefined;
    this.defaultEndsInText = _m('0s');
    this.state = {
      offer: undefined,
      endsInText: this.defaultEndsInText,
      willDeposit: false,
      displayedPromo: this.props.appliedPromo,
    };
  }

  componentDidMount() {
    this._addListeners();

    this.appStateChangeListener = AppState.addEventListener('change', this._handleAppStateChange);

    if (this.props.isInsufficientFunds) {
      this._fetchDataForInsufficientFundsDisplay();
    } else {
      this._determineOfferToDisplay(this.props.storeInfo);
    }
  }

  componentWillUnmount() {
    this._removeListeners();
    this._cancelTimer();

    if (this.appStateChangeListener) {
      this.appStateChangeListener.remove();
    }
  }

  componentDidUpdate(prevProps: ILTOComponentProps) {
    if (
      this.props.appliedPromo !== prevProps.appliedPromo ||
      this.props.storeInfo !== prevProps.storeInfo
    ) {
      if (this.props.storeInfo && !this.state.offer && !this.isTriggeringOffer) {
        this._determineOfferToDisplay(this.props.storeInfo);
      }

      if (Platform.OS !== 'web') {
        this.tileButtonContainer?.pulse(PROMO_BUMP_ANIMATION_DURATION);
      }

      setTimeout(() => {
        this.setState({
          displayedPromo: this.props.appliedPromo,
        });
      }, PROMO_BUMP_ANIMATION_UPDATE_OFFSET);
    }
  }

  _handleAppStateChange = (nextAppState: unknown) => {
    if (nextAppState === 'active') {
      this._addListeners();
      if (this.props.storeInfo && !this.state.offer && !this.isTriggeringOffer) {
        this._determineOfferToDisplay(this.props.storeInfo);
      }
    } else {
      this._removeListeners();
    }
  };

  _addListeners() {
    this._removeListeners();
    this.subscriptions = [];
    this.subscriptions.push(
      GlobalEventEmitter.addListener('StoreFrontViewDidAppear', (event: Object) => {
        this._fetchDataForInsufficientFundsDisplay();
      })
    );
    if (Platform.OS === 'web') {
      return;
    }
    this.subscriptions.push(
      GlobalEventEmitter.addListener('HomeScreenDidAppear', (event: Object) => {
        if (!this.props.isInsufficientFunds) {
          this._determineOfferToDisplay(this.props.storeInfo);
        } else {
          this._fetchDataForInsufficientFundsDisplay();
        }
      })
    );
  }

  _removeListeners() {
    if (this.subscriptions) {
      this.subscriptions.forEach((listener) => {
        if (listener) {
          listener.remove();
        }
      });
      this.subscriptions = [];
    }
  }

  _fetchDataForInsufficientFundsDisplay(useCache: boolean = false) {
    this.setState({
      ...this.state,
      storeInfo: {},
    });
  }

  _getOfferToDisplay(offers: Array<Object>): Object | undefined {
    if (offers && Array.isArray(offers) && offers.length > 0) {
      const triggeredOffer = this._filterTriggeredOffer(offers);
      if (triggeredOffer && this._isValidOffer(triggeredOffer)) {
        return triggeredOffer;
      }
    }
    return undefined;
  }

  _determineOfferToDisplay(storeInfo: any) {
    // TODO (websdk-post-mvp): Switch back to limited_time_offers when proper api version is available
    const offers = _.get(storeInfo, 'limited_time_offers', []);
    if (!this.state.willDeposit && offers && Array.isArray(offers) && offers.length > 0) {
      const triggeredOffer = this._getOfferToDisplay(offers);
      if (triggeredOffer) {
        this._endLoadingForOffer(storeInfo, triggeredOffer);
      } else {
        this._triggerAppropriateOffer(storeInfo, offers);
      }
    } else {
      this._endLoadingAndHide({storeInfo: storeInfo, offer: null});
    }
  }

  _endLoadingForOffer(storeInfo: any, offer: any, completion?: () => void) {
    const updatedStoreInfo = storeInfo;
    // TODO (websdk-post-mvp): Switch back to limited_time_offers when proper api version is available
    const offers = _.get(storeInfo, 'limited_time_offers', []);
    if (offers.length > 0) {
      const idOffers = _.map(offers, (singleOffer: any): any => {
        if (singleOffer.id == offer.offer_id) {
          singleOffer.end = offer.end;
        }
        return singleOffer;
      });
      updatedStoreInfo.limited_time_offers = idOffers;
    }

    this.setState(
      {
        offer: offer,
        storeInfo: updatedStoreInfo,
      },
      () => {
        if (completion) {
          completion();
        }
        if (offer && offer.end) {
          this._startCountdownTimer();
        }
      }
    );
  }

  _endLoadingAndHide(state: any, completion?: () => void) {
    if (this.state.offer != state.offer) {
      SKZLayoutAnimations.easeInEaseOut();
    }
    this._cancelTimer();
    setTimeout(() => {
      this.setState(state, () => {
        if (completion) {
          completion();
        }
      });
    }, SKZConstants.MS_PER_HALF_SEC);
  }

  _startCountdownTimer() {
    this._cancelTimer();
    this.offerCountdownTimer = setInterval(() => {
      if (this.state.offer && this.state.offer.end) {
        const endDate = new Date(this.state.offer.end);
        if (moment() < moment(endDate)) {
          this.setState({
            endsInText: I18n.getSimplifiedTime(this.state.offer.end),
          });
        } else {
          this._endLoadingAndHide({storeInfo: this.state.storeInfo, offer: null}, () => {
            // LTOComponent needs to tell HomeScreen that the LTO expired so that HomeScreen
            // can update the LTO's data, not get updated from within the component.
            // But for LTO's not on Play, get updated data for the component as expected
            GlobalEventEmitter.emit('LTOExpired');
            if (!this.props.isPlayTabDisplayed) {
              this._fetchDataForInsufficientFundsDisplay();
            }
          });
        }
      } else {
        this._cancelTimer();
      }
    }, SKZConstants.MS_PER_SEC);
  }

  _cancelTimer() {
    if (this.offerCountdownTimer) {
      clearInterval(this.offerCountdownTimer);
      this.offerCountdownTimer = undefined;
    }
  }

  _filterTriggeredOffer(offers: Array<any>): any {
    let triggeredOffer = null;
    _.forEach(offers, (offer: any) => {
      if (offer.end) {
        triggeredOffer = offer;
      }
    });

    return triggeredOffer;
  }

  //TODO:  Testable method!
  _findAppropriateOfferToTrigger(offers: Array<any>): any {
    let offer = null;
    offer = _.find(offers, {type: 'FIRST_TIME_USER'});
    if (!offer) {
      if (this.props.isInsufficientFunds) {
        //Cash Depleted can only be triggered on InsufficientFunds;
        offer = _.find(offers, {type: 'CASH_DEPLETED'});
      }
      //If no InsufficientFunds offer, fallback to ALL
      if (!offer) {
        offer = _.find(offers, {type: 'ALL'});
      }
    }

    return offer;
  }

  _triggerAppropriateOffer(storeInfo: Object, offers: Array<Object>) {
    //FTUE offer takes precedence
    const offer = this._findAppropriateOfferToTrigger(offers);

    if (!this.isTriggeringOffer) {
      if (offer) {
        this.isTriggeringOffer = true;
        const triggerPath = 'store/offers/' + offer.id + '/triggers';
        NetClient.postURL(triggerPath, null, 0, (error: Object, payload: any) => {
          if (payload && !error && this._isValidOffer(payload)) {
            offer.end = payload.end;
            this._endLoadingForOffer(storeInfo, offer, () => {
              this.isTriggeringOffer = false;
            });
          } else {
            this._endLoadingAndHide({storeInfo: storeInfo, offer: null}, () => {
              this.isTriggeringOffer = false;
            });
          }
        });
      } else {
        //End loading by setting storeInfo;
        this._endLoadingAndHide({storeInfo: storeInfo, offer: null});
      }
    }
  }

  _displayStore() {
    GlobalEventEmitter.emit('switchToTab', 'STORE');
  }

  _isValidOffer(offer: any): boolean {
    //Triggered offers will have an End Date, untriggered will be null.
    const now = new Date();
    const end = new Date(offer.end);
    return end > now;
  }

  _doneLoading(): boolean {
    // Don't show loading indicator for LTOs on PlayTab
    if (this.props.isPlayTabDisplayed) {
      return true;
    }
    return this.state.storeInfo !== undefined;
  }

  _claimOfferInsufficientFunds() {
    const storeInfo = this.state.storeInfo;
    const offer = this.state.offer;
    setTimeout(() => {
      CheckoutController.launchCheckoutFlow({
        storeInfo,
        offer,
        navigator: this.props.mainNavigator,
      });
    }, SKZConstants.SHORT_DELAY_MS);
  }

  _claimOffer() {
    const storeInfo = this.state.storeInfo;
    const offer = this.state.offer;
    const state = {
      storeInfo: null,
      offer: null,
      willDeposit: true,
      endsInText: this.defaultEndsInText,
    };
    this._endLoadingAndHide(state, () => {
      this.props.onClaimOffer(storeInfo, offer);
    });
  }

  _handlePhoneStoreClaim = () => {
    this._claimOffer();
    this._dismissController();
  };

  _shouldDisplayPromo(): boolean {
    return this.state.displayedPromo?.lto_compatible;
  }

  _getPromoBonusAmount(): string {
    const amount = CheckoutController.getPromoAmount(this.state.offer, this.state.displayedPromo);
    return I18n.getCurrencyString(amount, '$', true, false);
  }

  // --------------------- MODAL CONTENT --------------------------------------

  _renderLimitedTimeOfferHeader(): React.ReactNode {
    const offer = this.state.offer;
    return (
      <View
        style={[styles.ltoTextContainer, {backgroundColor: 'transparent'}]}
        accessible={true}
        accessibilityLabel={'limited_time_offer'}
      >
        <Label style={styles.headerText} accessible={false}>
          {I18n.message('Limited Time Offer!')}
        </Label>
        <Label style={styles.subheaderText} key={offer.amount} accessible={false}>
          {I18n.message('Deposit') +
            ' ' +
            I18n.getCurrencyString(offer.amount, '$', true, false) +
            ' ' +
            I18n.message('and get')}
        </Label>
      </View>
    );
  }

  _renderCountdown(): React.ReactNode {
    return (
      <View style={styles.countdownContainer}>
        <Label style={styles.countdownDetailText} accessible={false}>
          {I18n.message('Ends in')}
        </Label>
        <Label style={styles.countdownTimeText} key={this.state.endsInText} accessible={false}>
          {' ' + this.state.endsInText}
        </Label>
      </View>
    );
  }

  _renderLimitedTimeOfferContent(): React.ReactNode {
    return (
      <View style={styles.offerContainer}>
        {this._renderOffer()}
        {this._renderCountdown()}
        {this._renderClaimButton()}
      </View>
    );
  }

  _renderDivider(): React.ReactNode {
    return (
      <Divider
        style={styles.divider}
        dividerColor={SkillzStyle.fullStyles.viewDividerColorTwo.backgroundColor}
      />
    );
  }

  _renderClaimButton(): React.ReactNode {
    return (
      <PushButton
        onPress={() => {
          AWSManager.trackEvent('ADD_FUNDS_MODAL_NEXT', {result: 'HISTORICAL'});
          this._claimOfferInsufficientFunds();
          this._dismissController();
        }}
        colors={SkillzStyle.gradients.btnGradientOne}
        textStyle={[styles.buttonText, {paddingHorizontal: 4}]}
        style={styles.buttonComponent}
        title={I18n.message('CLAIM NOW')}
        cornerRadius={SkillzStyle.fullStyles.viewCornerRadiusPrimary.borderRadius}
        accessible={true}
        accessibilityLabel={'claim_now'}
      />
    );
  }

  // ----------------------- STOREFRONT CONTENT -------------------------------

  _renderLTOButtonContents(): React.ReactNode {
    return (
      <View style={styles.storeFrontLTOButtonContents}>
        {this._renderTabletStoreBonusAmount()}
        {this._renderTabletStoreDepositAmount()}
      </View>
    );
  }

  _renderTabletStoreBonusAmount(): React.ReactNode {
    return (
      <View style={{flex: 0.55, flexGrow: 1}}>
        <Label style={styles.bonusCashText} key={this.state.offer.bonus} accessible={false}>
          {I18n.getCurrencyString(this.state.offer.bonus, '$', true, false) +
            ' ' +
            I18n.message('BONUS CASH')}
        </Label>
        <Optional test={this._shouldDisplayPromo()}>
          <Label style={[styles.depositText, {marginBottom: 2}]}>
            {I18n.formattedMessage('+{0} PROMO BONUS CASH', this._getPromoBonusAmount())}
          </Label>
        </Optional>
      </View>
    );
  }

  _renderTabletStoreDepositAmount(): React.ReactNode {
    return (
      <View style={{flex: 0.45, flexGrow: 1}}>
        <Label style={styles.depositText} key={this.state.offer.amount} accessible={false}>
          {I18n.message('WITH A ') +
            I18n.getCurrencyString(this.state.offer.amount, '$', true, false) +
            I18n.message(' DEPOSIT')}
        </Label>
      </View>
    );
  }

  _renderLTOButton(): React.ReactNode {
    return (
      <Animatable.View
        style={styles.storeFrontLTOButtonWrapper}
        ref={(ref) => (this.tileButtonContainer = ref)}
        useNativeDriver={Platform.OS == 'ios'}
      >
        <PushButton
          style={styles.storeFrontLTOButton}
          cornerRadius={SkillzStyle.fullStyles.viewCornerRadiusPrimary.borderRadius}
          colors={SkillzStyle.gradients.btnGradientStore}
          onPress={() => {
            this.setState({
              storeInfo: this.props.storeInfo,
            });
            this._claimOffer();
          }}
          accessible={true}
          accessibilityLabel={'lto_button'}
        >
          {this._renderLTOButtonContents()}
        </PushButton>
      </Animatable.View>
    );
  }

  _renderLimitedTimeOfferText(): React.ReactNode {
    const showTimer = this.state.endsInText !== this.defaultEndsInText;
    return (
      <View style={styles.storeFrontLTOTextContainer}>
        <Label style={styles.limitedTimeOfferText} accessible={false}>
          {I18n.message('LIMITED TIME OFFER!')}
        </Label>
        {showTimer ? (
          <View style={{flexDirection: 'row', flexGrow: 1, justifyContent: 'flex-end'}}>
            <Label style={styles.endsInText} accessible={false}>
              {I18n.message('Ends In: ')}
            </Label>
            <Label
              style={styles.storeFrontCountdownText}
              key={this.state.endsInText}
              accessible={false}
            >
              {this.state.endsInText}
            </Label>
          </View>
        ) : null}
      </View>
    );
  }

  _renderStoreFrontContent(): React.ReactNode {
    if (Sizes.isTablet()) {
      return (
        <View style={styles.storeFrontLTOContainer}>
          <View style={styles.storeFrontLTO}>
            {this._renderLimitedTimeOfferText()}
            {this._renderLTOButton()}
          </View>
        </View>
      );
    }
    return this._renderPhoneStoreFrontContent();
  }

  _renderPhoneStoreFrontContent(): React.ReactNode {
    return (
      <View style={styles.backingView}>
        <TouchableOpacity
          style={styles.touchableOpacity}
          accessible={false}
          activeOpacity={0.55}
          onPress={this._handlePhoneStoreClaim}
        >
          <View style={styles.mainView} testID={'deposit_lto'}>
            {this._renderStorePhoneOfferTitle()}
            {this._renderPhoneStoreOffer()}
          </View>
        </TouchableOpacity>
      </View>
    );
  }

  _renderStorePhoneOfferTitle(): React.ReactNode {
    const showTimer = this.state.endsInText !== this.defaultEndsInText;
    return (
      <View style={styles.topView}>
        <Label style={styles.limitedTimeOfferTextPhone} accessible={true}>
          {I18n.message('LIMITED TIME OFFER!')}
        </Label>
        {showTimer ? (
          <Label key={this.state.endsInText} style={styles.endsInTextPhone} accessible={true}>
            {this.state.endsInText}
          </Label>
        ) : null}
      </View>
    );
  }

  _renderPhoneStoreOffer(): React.ReactNode {
    return (
      <Animatable.View
        style={styles.gradientFrame}
        ref={(ref) => (this.tileButtonContainer = ref)}
        useNativeDriver={Platform.OS == 'ios'}
      >
        <LinearGradient
          style={styles.gradient}
          colors={SkillzStyle.gradients.btnGradientStore}
          accessible={true}
          accessibilityLabel={'bonus_cash_green_button'}
        >
          {this._renderPhoneStoreBonusAmount()}
          {this._renderPhoneStoreDepositAmount()}
        </LinearGradient>
      </Animatable.View>
    );
  }

  _renderPhoneStoreBonusAmount(): React.ReactNode {
    return (
      <View style={styles.phoneStoreBonusCashWrapper}>
        <Label style={styles.bonusCashTextPhone} key={this.state.offer.bonus} accessible={true}>
          {I18n.getCurrencyString(this.state.offer.bonus, '$', true, false) +
            ' ' +
            I18n.message('BONUS CASH')}
        </Label>
        <Optional test={this._shouldDisplayPromo()}>
          <Label style={styles.promoBonusCashTextPhone}>
            {I18n.formattedMessage('+{0} PROMO BONUS CASH', this._getPromoBonusAmount())}
          </Label>
        </Optional>
      </View>
    );
  }

  _renderPhoneStoreDepositAmount(): React.ReactNode {
    return (
      <View style={styles.depositFrame}>
        <Label style={styles.depositAmountText} key={this.state.offer.amount} accessible={true}>
          {I18n.formattedMessage(
            'with a {0}',
            I18n.getCurrencyString(this.state.offer.amount, '$', true, false)
          )}
        </Label>
        <Label style={styles.depositTextPhone} accessible={true}>
          {I18n.message('DEPOSIT')}
        </Label>
      </View>
    );
  }

  // --------------------------------------------------------------------------

  _renderContent(): React.ReactNode | null {
    if (this.props.isInsufficientFunds) {
      if (this.props.isCash && this.state.offer) {
        return this._renderInsufficientFundsOffer();
      }
      //No LTO available, so render 'DEPOSIT NOW' button for InsufficientFunds.
      return this._renderInsufficientFundsNoLTODisplay();
    } else if (this.state.offer) {
      return this._renderStoreFrontContent();
    }

    return false;
  }

  _renderInsufficientFundsOffer(): React.ReactNode {
    if (Sizes.isLandscape() && Sizes.isPhone()) {
      return (
        <View style={styles.contentContainer}>
          {this._renderLimitedTimeOfferHeader()}
          {this._renderLimitedTimeOfferContent()}
        </View>
      );
    }
    return (
      <View style={styles.contentContainer}>
        {this._renderDivider()}
        {this._renderLimitedTimeOfferHeader()}
        {this._renderLimitedTimeOfferContent()}
      </View>
    );
  }

  _dismissController() {
    if (this.props.onDone) {
      this.props.onDone();
    }
  }

  _popToStore() {
    OrientationTransitionController.updateOrientationToPortrait();
    this.props.onDone();
    setTimeout(() => {
      NativeNavigation.gotoDestination('Store');
      NavigationManager.popToRoot();
    }, SKZConstants.SHORT_DELAY_MS);
  }

  _renderInsufficientFundsNoLTODisplay(): React.ReactNode {
    let id = 'deposit_now';
    let message = 'DEPOSIT NOW';
    const VC_ONLY = ClientPropertyConstants.getIsVirtualCurrencyOnly();
    if (VC_ONLY) {
      message = 'GET MORE';
      id = 'get_more';
    }
    return (
      <PushButton
        onPress={() => {
          AWSManager.trackEvent('ADD_FUNDS_MODAL_DEPOSIT_NOW');
          this._popToStore();
        }}
        colors={SkillzStyle.fullStyles.btnGradientOne}
        style={[styles.buttonComponent, {width: 220}]}
        cornerRadius={SkillzStyle.fullStyles.viewCornerRadiusPrimary.borderRadius}
        textStyle={styles.buttonText}
        title={I18n.message(message)}
        accessible={true}
        accessibilityLabel={id}
      />
    );
  }

  _renderOffer(): React.ReactNode {
    const offer = this.state.offer;
    return (
      <View style={styles.depositTile}>
        <DepositOfferTile
          isExtraLarge={true}
          isBestOffer={true}
          primaryText={I18n.getCurrencyString(offer.bonus, '$', true, false)}
          subText={I18n.message('BONUS CASH')}
          isExtendedWidth={false}
          key={offer.id}
          onChange={() => {}}
          pointerEvents='none'
        />
      </View>
    );
  }

  render(): React.ReactNode {
    return (
      <View
        style={[
          styles.mainContainer,
          this.props.isPlayTabDisplayed ? styles.mainContainerPlayTab : {},
        ]}
        accessible={false}
      >
        <LoadableComponent
          shouldRender={this._doneLoading.bind(this)}
          renderChildren={this._renderContent.bind(this)}
        />
      </View>
    );
  }
}
